import { Crown, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackPurchase } from "@/lib/analytics";
import PaypalSubscribeButton from "./PaypalSubscribeButton";

const PERKS = [
  "1,500 gold welcome bonus — one-time, on your first VIP activation",
  "+100 gold every month while your VIP is active",
  "Gain +5 gladiator slots while VIP is active",
  "Upload a custom profile avatar",
  "Fight cooldown halved — twice as many bouts per day",
  "Extra slot for completing 2x quests!",
  "Exclusive VIP badge on your lanista profile",
];

export default function VipPanel() {
  const { user, refreshUser } = useAuth();
  const isVip = user?.vip;

  const onSuccess = async (res) => {
    trackPurchase({ value: 1.99, currency: "USD", transactionId: res?.subscription_id, itemType: "vip_subscription" });
    if (res?.vip) toast.success("Welcome to VIP — perks unlocked!");
    else toast.success("Subscription received — VIP activates within moments.");
    await refreshUser();
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your VIP membership? Perks end and your roster cap reduces by 5.")) return;
    try {
      await api.post("/paypal/cancel");
      toast.success("VIP membership cancelled.");
      await refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (isVip) {
    return (
      <div data-testid="vip-active-card"
        className="relative overflow-hidden border border-amber-500/50 bg-gradient-to-br from-amber-950/40 to-zinc-950 p-6 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Crown size={20} className="text-amber-400 fill-amber-500/40" />
          <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-lg">VIP Lanista — Active</h3>
        </div>
        <p className="text-sm text-zinc-300 mb-4">Your champions enjoy every VIP advantage. Thank you for supporting the Arena.</p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-5">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-zinc-300">
              <Check size={15} className="text-emerald-400 mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>
        <button data-testid="vip-cancel-btn" onClick={cancel}
          className="text-xs uppercase tracking-widest text-zinc-500 hover:text-red-400 transition-colors">
          Manage / Cancel membership
        </button>
      </div>
    );
  }

  return (
    <div data-testid="vip-promo-card"
      className="relative overflow-hidden border border-amber-700/40 bg-gradient-to-br from-amber-950/30 to-zinc-950 p-6 mb-8">
      <div className="grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Crown size={22} className="text-amber-400 fill-amber-500/40" />
            <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-xl">Become a VIP Lanista</h3>
          </div>
          <p className="text-amber-200/90 font-cinzel tracking-wide mb-1">
            $1.99 first month <span className="text-zinc-500">·</span> then $4.49/mo
          </p>
          <p className="text-sm text-zinc-400 mb-4">Cancel anytime. Dominate the arena with elite perks.</p>
          <ul className="space-y-1.5">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-zinc-300">
                <Check size={15} className="text-amber-400 mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-950/60 border border-amber-900/40 p-4 rounded-sm">
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3 text-center">Subscribe with PayPal</p>
          <PaypalSubscribeButton onSuccess={onSuccess} />
        </div>
      </div>
    </div>
  );
}
